import { useEffect, useMemo, useState } from "react";
import { CameraTimeline } from "./RouteCameras.jsx";
import { camerasAlongRoute } from "./routeCameras.js";
import { loadRoadRoute } from "./routing.js";
import { compareStationToBest } from "./insights.js";
import { formatEuro, formatKm, formatLitres, formatSignedEuro, tidyAddress, titleCase } from "./format.js";
import { BrandBadge, Icon, Price, PRICE_KINDS, PriceTag } from "./ui.jsx";

export const directionsUrl = (station) => `https://www.google.com/maps/dir/?api=1&destination=${station.lat},${station.lng}`;

export function stationName(station) {
  return titleCase(station.name);
}

export function stationAddress(station) {
  return tidyAddress(station.address);
}

function travelLabel(tripMode) {
  if (tripMode === "roundTrip") return "ida y vuelta";
  if (tripMode === "oneWay") return "solo ida";
  return "de desvío";
}

function StationCameras({ station, origin, tripMode, cameras, cameraLoading, cameraError, onRequestCameras }) {
  const [leg, setLeg] = useState("outbound");
  const effectiveLeg = tripMode === "roundTrip" ? leg : "outbound";
  const [route, setRoute] = useState(null);
  const [routeError, setRouteError] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    onRequestCameras();
    setRoute(null);
    setRouteError(false);
    const [from, to] = effectiveLeg === "return" ? [station, origin] : [origin, station];
    loadRoadRoute(from, to, controller.signal)
      .then((value) => { if (!controller.signal.aborted) setRoute(value); })
      .catch((error) => { if (!controller.signal.aborted && error.name !== "AbortError") setRouteError(true); });
    return () => controller.abort();
    // onRequestCameras cambia en cada render del padre; solo importa al abrir o cambiar de tramo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveLeg, origin.lat, origin.lng, station.id]);
  const items = useMemo(() => camerasAlongRoute(cameras, route?.geometry), [cameras, route]);
  return (
    <div className="row-cameras">
      {tripMode === "roundTrip" && (
        <div className="mini-seg" role="group" aria-label="Tramo del desplazamiento">
          <button type="button" aria-pressed={leg === "outbound"} onClick={() => setLeg("outbound")}>Ida</button>
          <button type="button" aria-pressed={leg === "return"} onClick={() => setLeg("return")}>Vuelta</button>
        </div>
      )}
      <p className="fine">Cámaras de la DGT junto al camino. Algunas enfocan el sentido contrario.</p>
      {routeError ? <p className="fine">No se pudo calcular el camino por carretera. Cierra y vuelve a abrir esta sección para reintentarlo.</p>
        : !route ? <p className="fine">Calculando el camino…</p>
        : cameraLoading ? <p className="fine">Consultando cámaras de la DGT…</p>
        : cameraError ? <p className="fine">{cameraError}</p>
        : <CameraTimeline items={items} compact />}
    </div>
  );
}

export function BestCard({ station, label, insights, tripMode }) {
  const estimate = station.estimate;
  const next = insights?.next;
  return (
    <article className="best-card" aria-label={`${label}: ${stationName(station)}`}>
      <div className="best-head">
        <BrandBadge station={station} />
        <div className="best-title">
          <span className="best-label">{label}</span>
          <h2>{stationName(station)}</h2>
          <p>{stationAddress(station)}</p>
        </div>
      </div>
      <div className="best-prices">
        <div>
          <Price value={estimate.effectivePrice} kind="trip" className="is-hero" />
          <PriceTag kind="trip" />
        </div>
        <div className="pump-price">
          <Price value={station.pumpPrice} kind="pump" unit={false} />
          <PriceTag kind="pump" />
        </div>
      </div>
      <dl className="facts">
        <div><dt>{travelLabel(tripMode)}</dt><dd>{formatKm(estimate.travelKm)}</dd></div>
        <div><dt>de viaje</dt><dd>+{formatEuro(estimate.travelCost)}</dd></div>
        <div><dt>por {formatEuro(estimate.purchasedLitres * station.pumpPrice).replace(",00", "")}</dt><dd>{formatLitres(estimate.purchasedLitres)}</dd></div>
      </dl>
      {next && Number.isFinite(insights.savingsVsNext) && (
        <p className="best-note">
          {insights.tight
            ? <>Casi empate: <b>{formatEuro(insights.savingsVsNext)}</b> menos que {stationName(next)}.</>
            : <>Ahorras <b>{formatEuro(insights.savingsVsNext)}</b> frente a la siguiente, {stationName(next)}.</>}
        </p>
      )}
      <a className="button is-primary" href={directionsUrl(station)} target="_blank" rel="noreferrer">
        <Icon name="nav" /> Cómo llegar
      </a>
    </article>
  );
}

export function StationRow({ station, position, best, isBest, selected, onSelect, tripMode, origin, cameraProps, primary = "trip" }) {
  const [camerasOpen, setCamerasOpen] = useState(false);
  useEffect(() => { if (!selected) setCamerasOpen(false); }, [selected]);
  const comparison = compareStationToBest(station, best);
  const estimate = station.estimate;
  return (
    <li className={`station-row${selected ? " is-selected" : ""}${isBest ? " is-best" : ""}`} id={`station-${station.id}`}>
      <button type="button" className="row-main" onClick={onSelect} aria-expanded={selected}>
        <span className="row-rank">{position}</span>
        <BrandBadge station={station} />
        <span className="row-text">
          <span className="row-name">{stationName(station)}{isBest && <span className="tag is-best">Mejor</span>}</span>
          <span className="row-sub">{formatKm(estimate.oneWayRoadKm)} · {stationAddress(station)}</span>
        </span>
        <span className="row-price">
          {primary === "pump" ? (
            <>
              <Price value={station.pumpPrice} kind="pump" unit={false} />
              <span className="caption is-trip"><Price value={estimate.effectivePrice} kind="trip" unit={false} className="is-inline" /> {PRICE_KINDS.trip.short}</span>
            </>
          ) : (
            <>
              <Price value={estimate.effectivePrice} kind="trip" unit={false} />
              <span className="caption is-pump"><Price value={station.pumpPrice} kind="pump" unit={false} className="is-inline" /> {PRICE_KINDS.pump.short}</span>
            </>
          )}
        </span>
      </button>
      {selected && (
        <div className="row-detail">
          <p>
            {isBest ? "Es la mejor opción contando el desplazamiento." : comparison
              ? <>Frente a la mejor: <b>{formatSignedEuro(comparison.totalDifference)}</b> en total{comparison.travelKmDifference > 0.05 ? `, ${formatKm(comparison.travelKmDifference)} más de viaje` : ""}.</>
              : null}
            {" "}{formatKm(estimate.travelKm)} {travelLabel(tripMode)} · +{formatEuro(estimate.travelCost)} de viaje.
          </p>
          <div className="row-actions">
            <a className="button is-small is-accent" href={directionsUrl(station)} target="_blank" rel="noreferrer"><Icon name="nav" /> Cómo llegar</a>
            {origin && (
              <button type="button" className="button is-small is-quiet" aria-expanded={camerasOpen} onClick={() => setCamerasOpen((value) => !value)}>
                <Icon name="camera" /> {camerasOpen ? "Ocultar cámaras" : "Cámaras del camino"}
              </button>
            )}
          </div>
          {camerasOpen && origin && <StationCameras station={station} origin={origin} tripMode={tripMode} {...cameraProps} />}
        </div>
      )}
    </li>
  );
}

export function SkeletonRows({ count = 5 }) {
  return (
    <ul className="station-list is-skeleton" aria-hidden="true">
      {Array.from({ length: count }, (_, index) => <li key={index} className="skeleton-row"><span /><span /><span /></li>)}
    </ul>
  );
}
